import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import FeedbackWidget from "@/components/FeedbackWidget";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Download, Heart, FileDown, Users, Sun, Home, Image, Pill, Activity, CalendarDays, ListTodo, NotebookPen, Contact, Receipt, Stethoscope, FolderLock, LayoutGrid, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import DocumentVault from "@/components/DocumentVault";
import MembersPanel from "@/components/MembersPanel";
import RemindersPanel from "@/components/RemindersPanel";
import PhotoTimeline from "@/components/PhotoTimeline";

const TABS = [
  { key: "overview", label: "Overview", icon: Home },
  { key: "photos", label: "Moments", icon: Image },
  { key: "medications", label: "Medications", icon: Pill },
  { key: "vitals", label: "Vitals", icon: Activity },
  { key: "appointments", label: "Appointments", icon: CalendarDays },
  { key: "tasks", label: "Tasks", icon: ListTodo },
  { key: "care_logs", label: "Care log", icon: NotebookPen },
  { key: "contacts", label: "Contacts", icon: Contact },
  { key: "expenses", label: "Expenses", icon: Receipt },
  { key: "doctor_notes", label: "Doctor's notes", icon: Stethoscope },
  { key: "documents", label: "Documents", icon: FolderLock },
  { key: "members", label: "Family & care team", icon: Users },
];

const FIELDS = {
  medications: [
    { k: "name", l: "Name", req: true }, { k: "dosage", l: "Dosage" },
    { k: "frequency", l: "Frequency" }, { k: "time_of_day", l: "Time of day" },
    { k: "refill_date", l: "Refill date", t: "date" }, { k: "notes", l: "Notes", area: true },
  ],
  vitals: [
    { k: "type", l: "Type", req: true, placeholder: "blood_pressure, weight, glucose, heart_rate" },
    { k: "value", l: "Value", req: true }, { k: "unit", l: "Unit" },
    { k: "recorded_at", l: "When", t: "datetime-local" }, { k: "notes", l: "Notes", area: true },
  ],
  appointments: [
    { k: "doctor", l: "Doctor", req: true }, { k: "specialty", l: "Specialty" },
    { k: "location", l: "Location" }, { k: "date", l: "When", t: "datetime-local", req: true },
    { k: "notes", l: "Notes", area: true },
  ],
  tasks: [
    { k: "title", l: "Title", req: true }, { k: "description", l: "Description", area: true },
    { k: "due_date", l: "Due date", t: "date" }, { k: "assigned_to", l: "Assigned to" },
    { k: "priority", l: "Priority", placeholder: "low, normal, high" },
  ],
  care_logs: [
    { k: "entry", l: "What happened today", area: true, req: true },
    { k: "mood", l: "Mood" }, { k: "recorded_at", l: "When", t: "datetime-local" },
  ],
  contacts: [
    { k: "name", l: "Name", req: true }, { k: "relationship", l: "Relationship" },
    { k: "phone", l: "Phone" }, { k: "email", l: "Email" },
    { k: "role", l: "Role", placeholder: "caregiver, emergency, doctor" },
    { k: "notes", l: "Notes", area: true },
  ],
  expenses: [
    { k: "amount", l: "Amount", t: "number", req: true },
    { k: "category", l: "Category", req: true, placeholder: "Medical, Pharmacy, Home Care…" },
    { k: "description", l: "Description" }, { k: "date", l: "Date", t: "date" },
  ],
  doctor_notes: [
    { k: "doctor", l: "Doctor", req: true }, { k: "date", l: "Date", t: "date" },
    { k: "summary", l: "Summary" }, { k: "content", l: "Notes", area: true },
  ],
  documents: [
    { k: "title", l: "Title", req: true }, { k: "category", l: "Category", placeholder: "medical, legal, insurance" },
    { k: "description", l: "Description", area: true },
  ],
};

export default function ChestDetail() {
  const { chestId } = useParams();
  const nav = useNavigate();
  const [chest, setChest] = useState(null);
  const [data, setData] = useState({});
  const [allChests, setAllChests] = useState([]);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [tab, setTab] = useState("overview");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({});

  const loadAll = async () => {
    try {
      const { data: c } = await api.get(`/hope-chests/${chestId}`);
      setChest(c);
      const collections = Object.keys(FIELDS);
      const results = await Promise.all(
        collections.map((k) => api.get(`/hope-chests/${chestId}/${k.replace("_", "-")}`).then((r) => [k, r.data]).catch(() => [k, []]))
      );
      setData(Object.fromEntries(results));
      // Also load other chests for the switcher
      api.get("/hope-chests").then((r) => setAllChests(r.data)).catch(() => { /* noop */ });
    } catch (e) {
      toast.error("Could not load Hope Chest");
      nav("/dashboard");
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [chestId]);

  const openAdd = () => { setForm({}); setDialogOpen(true); };

  const submit = async () => {
    const fields = FIELDS[tab];
    for (const f of fields) {
      if (f.req && !form[f.k]) { toast.error(`${f.l} is required`); return; }
    }
    try {
      const payload = { ...form };
      if (tab === "expenses" && payload.amount) payload.amount = parseFloat(payload.amount);
      await api.post(`/hope-chests/${chestId}/${tab.replace("_", "-")}`, payload);
      toast.success("Saved");
      setDialogOpen(false); setForm({});
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    }
  };

  const remove = async (itemId) => {
    if (!window.confirm("Remove this entry?")) return;
    try {
      await api.delete(`/hope-chests/${chestId}/${tab.replace("_", "-")}/${itemId}`);
      toast.success("Removed");
      loadAll();
    } catch (err) {
      toast.error("Could not remove");
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/hope-chests/${chestId}/export/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `hope-chest-${chest.name}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch {
      toast.error("Could not export PDF");
    }
  };

  if (!chest) return <AppShell chestId={chestId}><div className="p-10 text-[#7A736E]">Loading…</div></AppShell>;

  return (
    <AppShell chestId={chestId}>
      <FeedbackWidget />
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-10">
        {/* Quick nav row */}
        <div className="flex items-center justify-between mb-5 gap-3 flex-wrap" data-testid="chest-quick-nav">
          <button
            onClick={() => nav("/dashboard")}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#EBE5DF] hover:bg-[#F3EFEA] text-sm text-[#3D3935] transition"
            data-testid="back-to-dashboard"
          >
            <LayoutGrid className="w-4 h-4" /> All Hope Chests
          </button>
          {allChests.length > 1 && (
            <div className="relative">
              <button
                onClick={() => setSwitcherOpen((v) => !v)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#EBE5DF] hover:bg-[#F3EFEA] text-sm text-[#3D3935] transition"
                data-testid="chest-switcher-button"
              >
                Switch chest <ChevronDown className="w-4 h-4" />
              </button>
              {switcherOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setSwitcherOpen(false)} />
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-2xl shadow-xl border border-[#EBE5DF] z-20 py-1.5 max-h-[300px] overflow-y-auto" data-testid="chest-switcher-menu">
                    {allChests.map((c) => {
                      const isCurrent = c.id === chestId;
                      return (
                        <button
                          key={c.id}
                          onClick={() => { setSwitcherOpen(false); if (!isCurrent) nav(`/chests/${c.id}`); }}
                          className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between gap-2 transition ${isCurrent ? "bg-[#F3EFEA] text-[#3A5A4A] font-medium" : "hover:bg-[#F3EFEA] text-[#3D3935]"}`}
                          data-testid={`chest-switcher-item-${c.id}`}
                        >
                          <span className="truncate">{c.name}</span>
                          {isCurrent && <span className="text-xs text-[#7A736E]">current</span>}
                        </button>
                      );
                    })}
                    <div className="border-t border-[#EBE5DF] mt-1.5 pt-1.5">
                      <button
                        onClick={() => { setSwitcherOpen(false); nav("/dashboard"); }}
                        className="w-full text-left px-4 py-2.5 text-sm text-[#3A5A4A] hover:bg-[#F3EFEA] transition flex items-center gap-2"
                        data-testid="chest-switcher-add"
                      >
                        <LayoutGrid className="w-3.5 h-3.5" /> See all & add new
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Header */}
        <div className="hc-card p-6 md:p-8 mb-8">
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            <div className="w-20 h-20 rounded-3xl bg-[#F3EFEA] flex items-center justify-center">
              <Heart className="w-8 h-8 text-[#C87B67]" fill="#C87B67" />
            </div>
            <div className="flex-1">
              <p className="text-xs uppercase tracking-[0.2em] text-[#7A736E]">Hope Chest</p>
              <h1 className="font-display text-3xl md:text-4xl font-semibold mt-1" data-testid="chest-detail-name">{chest.name}</h1>
              <p className="text-[#7A736E] mt-1">{chest.relationship} {chest.dob && `• Born ${chest.dob}`} {chest.blood_type && `• Blood ${chest.blood_type}`}</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button onClick={() => nav(`/care-mode/${chestId}`)} variant="outline" className="rounded-full border-[#D9A441] text-[#3A5A4A] hover:bg-[#E8DCC8]/50" data-testid="open-care-mode">
                <Sun className="w-4 h-4 mr-2" /> Open Care Mode
              </Button>
              <Button onClick={downloadPdf} variant="outline" className="rounded-full border-[#EBE5DF]" data-testid="chest-export-pdf">
                <FileDown className="w-4 h-4 mr-2" /> Export PDF
              </Button>
              <Button onClick={() => nav(`/planner/${chestId}`)} className="rounded-full bg-[#3A5A4A] hover:bg-[#2C4538]" data-testid="chest-open-planner">
                Open Hope Planner
              </Button>
            </div>
          </div>
          {(chest.conditions || chest.allergies) && (
            <div className="grid md:grid-cols-2 gap-4 mt-6 pt-6 border-t border-[#EBE5DF]">
              {chest.conditions && <div><p className="text-xs uppercase tracking-wider text-[#7A736E] mb-1">Conditions</p><p className="text-sm">{chest.conditions}</p></div>}
              {chest.allergies && <div><p className="text-xs uppercase tracking-wider text-[#7A736E] mb-1">Allergies</p><p className="text-sm text-[#C87B67]">{chest.allergies}</p></div>}
            </div>
          )}
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="bg-transparent flex-wrap h-auto gap-1 justify-start mb-6 p-0">
            {TABS.map((t) => {
              const isPremiumTab = t.key === "photos" || t.key === "members";
              const Icon = t.icon;
              return (
                <TabsTrigger
                  key={t.key}
                  value={t.key}
                  className="rounded-full px-4 py-2 data-[state=active]:bg-[#3A5A4A] data-[state=active]:text-white text-[#7A736E] hover:text-[#3D3935] relative flex items-center gap-1.5"
                  data-testid={`tab-${t.key}`}
                >
                  {Icon && <Icon className="w-4 h-4" />}
                  {t.label}
                  {isPremiumTab && <span className="ml-1.5 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#E8DCC8] text-[#3A5A4A] font-medium">Premium</span>}
                </TabsTrigger>
              );
            })}
          </TabsList>

          <TabsContent value="overview">
            <div className="space-y-6">
              <RemindersPanel chestId={chestId} />
              <Overview data={data} />
            </div>
          </TabsContent>

          <TabsContent value="documents">
            <DocumentVault chestId={chestId} />
          </TabsContent>

          <TabsContent value="members">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl">Family & care team</h2>
            </div>
            <MembersPanel chestId={chestId} />
          </TabsContent>

          <TabsContent value="photos">
            <PhotoTimeline chestId={chestId} />
          </TabsContent>

          {Object.keys(FIELDS).filter((k) => k !== "documents" && k !== "photos").map((key) => (
            <TabsContent key={key} value={key}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-xl">{TABS.find((t) => t.key === key)?.label}</h2>
                <Button onClick={openAdd} className="rounded-full bg-[#3A5A4A] hover:bg-[#2C4538]" data-testid={`add-${key}-button`}>
                  <Plus className="w-4 h-4 mr-2" /> Add
                </Button>
              </div>
              {key === "vitals" && data.vitals?.length > 0 && <VitalsChart vitals={data.vitals} />}
              <ListView items={data[key] || []} fields={FIELDS[key]} onDelete={remove} testKey={key} />
            </TabsContent>
          ))}
        </Tabs>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="rounded-2xl max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Add {TABS.find((t) => t.key === tab)?.label}</DialogTitle>
              <DialogDescription>Fill what you have now — you can always come back and add more.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
              {(FIELDS[tab] || []).map((f) => (
                <div key={f.k}>
                  <Label className="text-sm">{f.l}{f.req && " *"}</Label>
                  {f.area ? (
                    <Textarea value={form[f.k] || ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} placeholder={f.placeholder} className="rounded-xl mt-1.5" data-testid={`field-${tab}-${f.k}`} />
                  ) : (
                    <Input type={f.t || "text"} value={form[f.k] || ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} placeholder={f.placeholder} className="rounded-xl mt-1.5" data-testid={`field-${tab}-${f.k}`} />
                  )}
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button onClick={submit} className="rounded-full bg-[#3A5A4A] hover:bg-[#2C4538]" data-testid={`submit-${tab}`}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppShell>
  );
}

function Overview({ data }) {
  const cards = [
    { label: "Medications", value: data.medications?.length || 0, color: "#D8E2D8" },
    { label: "Upcoming appts", value: data.appointments?.filter((a) => !a.completed).length || 0, color: "#E8DCC8" },
    { label: "Open tasks", value: data.tasks?.filter((t) => !t.completed).length || 0, color: "#F3EFEA" },
    { label: "Documents", value: data.documents?.length || 0, color: "#D8E2D8" },
  ];
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="hc-card p-6" data-testid={`overview-${c.label}`}>
            <div className="w-10 h-10 rounded-2xl mb-3" style={{ background: c.color }} />
            <p className="text-3xl font-display font-semibold">{c.value}</p>
            <p className="text-sm text-[#7A736E] mt-1">{c.label}</p>
          </div>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="hc-card p-6">
          <h3 className="font-display text-lg mb-4">Active medications</h3>
          {(data.medications || []).slice(0, 5).map((m) => (
            <div key={m.id} className="flex justify-between py-2 border-b border-[#EBE5DF] last:border-0">
              <div><p className="text-sm font-medium">{m.name}</p><p className="text-xs text-[#7A736E]">{m.dosage} • {m.frequency}</p></div>
            </div>
          ))}
          {(!data.medications || data.medications.length === 0) && <p className="text-sm text-[#7A736E]">None yet.</p>}
        </div>
        <div className="hc-card p-6">
          <h3 className="font-display text-lg mb-4">Recent care log</h3>
          {(data.care_logs || []).slice(0, 3).map((l) => (
            <div key={l.id} className="py-2 border-b border-[#EBE5DF] last:border-0">
              <p className="text-sm">{l.entry}</p>
              <p className="text-xs text-[#7A736E] mt-1">{l.mood && `${l.mood} • `}{new Date(l.created_at).toLocaleString()}</p>
            </div>
          ))}
          {(!data.care_logs || data.care_logs.length === 0) && <p className="text-sm text-[#7A736E]">No entries yet.</p>}
        </div>
      </div>
    </div>
  );
}

function VitalsChart({ vitals }) {
  // Group by type
  const grouped = {};
  vitals.forEach((v) => {
    if (!grouped[v.type]) grouped[v.type] = [];
    const num = parseFloat(v.value);
    if (!isNaN(num)) grouped[v.type].push({ name: (v.recorded_at || v.created_at || "").slice(0, 10), value: num });
  });
  const types = Object.keys(grouped);
  if (types.length === 0) return null;
  return (
    <div className="grid md:grid-cols-2 gap-4 mb-6">
      {types.slice(0, 4).map((t) => (
        <div key={t} className="hc-card p-5">
          <p className="text-sm font-medium capitalize">{t.replace("_", " ")}</p>
          <ResponsiveContainer width="100%" height={140}>
            <LineChart data={grouped[t]} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <XAxis dataKey="name" hide />
              <YAxis hide domain={["auto", "auto"]} />
              <Tooltip />
              <Line type="monotone" dataKey="value" stroke="#3A5A4A" strokeWidth={2} dot={{ fill: "#C87B67", r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ))}
    </div>
  );
}

function ListView({ items, fields, onDelete, testKey }) {
  if (!items || items.length === 0) {
    return <div className="hc-card p-10 text-center text-[#7A736E]" data-testid={`empty-${testKey}`}>Nothing here yet. Add your first entry.</div>;
  }
  return (
    <div className="grid gap-3">
      {items.map((it) => (
        <div key={it.id} className="hc-card p-5 flex justify-between items-start gap-4" data-testid={`item-${testKey}-${it.id}`}>
          <div className="flex-1 min-w-0">
            <div className="grid sm:grid-cols-2 gap-2">
              {fields.map((f) => it[f.k] && (
                <div key={f.k} className="min-w-0">
                  <p className="text-xs uppercase tracking-wider text-[#7A736E]">{f.l}</p>
                  <p className="text-sm break-words">{String(it[f.k])}</p>
                </div>
              ))}
            </div>
          </div>
          <button onClick={() => onDelete(it.id)} className="text-[#7A736E] hover:text-[#C87B67] transition" data-testid={`delete-${testKey}-${it.id}`}>
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  );
}
